"use client";
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { AnimatedSidebarPanel } from "../../layout/AnimatedSidebarPanel";
import { usePages } from "@/hooks/usePages";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { setSections } from "@/store/slices/webbuilderSlice";
import { PageDraft } from "@/types/models/page";
import { toast } from "sonner";
import {
  Clock,
  FileText,
  AlertTriangle,
  Save,
  RotateCcw,
  X,
  Calendar,
} from "lucide-react";

interface DraftsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  pageId?: string;
}

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (action: "cancel" | "save-and-replace" | "replace-only") => void;
  draftName: string;
}

function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  draftName,
}: ConfirmationDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Replace Current Content?
          </DialogTitle>
          <DialogDescription>
            You&apos;re about to replace the current page content with the draft
            &ldquo;{draftName}&rdquo;. This action will replace all sections and
            their data.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Warning:</strong> This will permanently replace your
              current work. Make sure you want to proceed.
            </p>
          </div>
        </div>
        <DialogFooter className="flex flex-col sm:flex-row sm:justify-end gap-2">
          <ButtonOutline
            onClick={() => onConfirm("cancel")}
            className="sm:w-[90px] text-sm"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            Cancel
          </ButtonOutline>
          <ButtonOutline
            onClick={() => onConfirm("save-and-replace")}
            className="w-auto min-w-[165px] bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30"
          >
            <Save className="w-3.5 h-3.5 mr-1" />
            Save Current & Replace
          </ButtonOutline>
          <ButtonPrimary
            onClick={() => onConfirm("replace-only")}
            className="sm:w-[120px] text-sm"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            Replace Only
          </ButtonPrimary>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DraftsPanel({ isOpen, onClose, pageId }: DraftsPanelProps) {
  const dispatch = useAppDispatch();
  const { actions } = usePages();
  // Safely access page slice to avoid runtime errors when a different Redux store is active
  const pageState = useAppSelector((state) => (state && (state as any).page) || null);
  const draftFormState = pageState?.draftForm || { loading: false, success: false, error: null };
  const drafts = Array.isArray(pageState?.drafts) ? pageState!.drafts : [];
  const draftsLoading = pageState?.draftsLoading || false;
  const draftsError = pageState?.draftsError || null;

  const [selectedDraft, setSelectedDraft] = useState<string | null>(null);

  // debug effect removed: drafts state logging

  // Fetch drafts when panel opens
  useEffect(() => {
    if (isOpen && pageId) {
      console.log("DraftsPanel - Fetching drafts for pageId:", pageId);
      actions.fetchDrafts(pageId);
    }
  }, [isOpen, pageId, actions]);

  const handleDraftSelect = (draft: PageDraft) => {
    setSelectedDraft(draft.id);
  };

  const handleConfirmation = async (
    action: "cancel" | "save-and-replace" | "replace-only",
  ) => {
    if (!selectedDraft || !pageId) return;

    try {
      if (action === "save-and-replace") {
        // Save current state as draft first
        actions.createDraft(pageId);

        // Wait for the draft creation to complete
        await new Promise<void>((resolve, reject) => {
          const checkSuccess = () => {
            if (draftFormState?.success) {
              resolve();
            } else if (draftFormState?.error) {
              reject(new Error(draftFormState.error.message));
            } else {
              setTimeout(checkSuccess, 100);
            }
          };
          checkSuccess();
        });
      }

      if (action === "save-and-replace" || action === "replace-only") {
        // Load the selected draft using the API action
        const response = await actions.getPageDraft(pageId, selectedDraft);

        if (response.success && response.data) {
          // Replace sections with draft sections
          dispatch(setSections(response.data.sections));

          toast.success(
            `Draft "${response.data.draft.description || "Untitled"}" loaded successfully!`,
          );
          onClose();
        } else {
          throw new Error("Failed to load draft");
        }
      }
    } catch (error) {
      console.error("Error handling draft:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to process draft",
      );
    } finally {
      setSelectedDraft(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <AnimatedSidebarPanel
        isOpen={isOpen}
        onClose={onClose}
        title="Page Drafts"
        subtitle="Manage and restore page drafts"
      >
        <div className="p-4">
          {draftsLoading ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-3">
              <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-muted-foreground">
                  Loading drafts...
                </p>
              </div>
            </div>
          ) : draftsError ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-3">
              <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-destructive">
                  Failed to load drafts
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {draftsError.message}
                </p>
              </div>
            </div>
          ) : !Array.isArray(drafts) || drafts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-3">
              <div className="w-12 h-12 bg-muted dark:bg-auth-background rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-muted-foreground">
                  You don&apos;t have any drafts yet. &ldquo;Drafts&rdquo; are
                  saved versions of your work.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                {drafts.length} draft{drafts.length !== 1 ? "s" : ""} available
              </div>

              {drafts.map((draft) => (
                <div
                  key={draft.id}
                  className="p-3 bg-muted dark:bg-auth-background border border-border dark:border-auth-background rounded-lg hover:bg-background dark:hover:bg-auth-card transition-colors cursor-pointer group"
                  onClick={() => handleDraftSelect(draft)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <h3 className="font-medium text-sm truncate">
                          {draft.description || "Untitled Draft"}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(draft.created_at)}</span>
                        {draft.is_active && (
                          <span className="bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full text-xs">
                            Active
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <RotateCcw className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </AnimatedSidebarPanel>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={selectedDraft !== null}
        onClose={() => setSelectedDraft(null)}
        onConfirm={handleConfirmation}
        draftName={
          selectedDraft
            ? drafts.find((d: PageDraft) => d.id === selectedDraft)?.description ||
              "Untitled Draft"
            : ""
        }
      />
    </>
  );
}
